import { useMemo, useState } from 'react';
import { Accordion, SelectOption, SelectProps } from '@ets/design-system';
import { useIntl } from 'react-intl';
import SearchUser, { UserOption } from '_common/components/SearchUser/SearchUser';
import { useDispatch, useSelector } from '_common/hooks';
import {
  DateRange,
  FilterIdentity,
  FilterName,
  OptionByType,
  SettingByType,
  TRANSLATIONS,
  TYPES,
} from '../../FilterController';
import { setFilter } from '../../FilterSlice';

const UserHandler = ({
  identity,
  filter,
  filterQuantity,
  options,
  settings,
  testId,
}: {
  identity: FilterIdentity;
  filter: FilterName;
  filterQuantity: number;
  options: OptionByType['user'];
  settings: SettingByType['user'];
  testId: string;
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [user, setUser] = useState<UserOption | UserOption[]>();

  const userSelections = useSelector((state) => {
    const value = state.filters[identity]?.[filter];
    return value as Exclude<typeof value, DateRange>; //Cast to avoid typescript false calls due to DateRange) as
  });

  const selectedUserIds = useMemo(
    () =>
      Array.isArray(userSelections)
        ? userSelections.map((selection) => selection.value)
        : userSelections?.value,
    [userSelections],
  );

  const handlerHasValue = selectedUserIds && selectedUserIds?.length > 0;

  //Workaround of SearchUser issues
  //Based on userId only, fetch info to construct a displayable value
  const currentValue = useMemo<UserOption[]>(() => {
    if (!selectedUserIds || selectedUserIds.length === 0) {
      return [];
    }

    //Options of removed and imported users
    let otherUsersOptions: UserOption[] = [];

    if (Array.isArray(selectedUserIds)) {
      selectedUserIds.forEach((selectedUserId) => {
        const userOption = Array.isArray(user)
          ? user.find((u) => u.value === selectedUserId)
          : user;
        otherUsersOptions.push({
          ...userOption,
          name: userOption?.name || selectedUserId,
          value: selectedUserId,
          label: selectedUserId,
          type: 'user',
        } as UserOption);
      });
    } else {
      otherUsersOptions.push({
        name: selectedUserIds,
        value: selectedUserIds,
        type: 'user',
      } as UserOption);
    }

    return [...otherUsersOptions];
  }, [selectedUserIds, user]);

  const handleValueChange: SelectProps<SelectOption, boolean>['onChange'] = (selectedUser) => {
    setUser(selectedUser as UserOption[]);
    if (Array.isArray(selectedUser)) {
      dispatch(
        setFilter({
          identity,
          filter,
          value: selectedUser
            .filter((user) => !!user.value)
            .map((user) => ({ value: user.value ?? '', label: user.name || user.label })),
        }),
      );
    } else {
      dispatch(
        setFilter({
          identity,
          filter,
          value: selectedUser
            ? {
                value: (selectedUser as UserOption).value,
                label: (selectedUser as UserOption).name ?? (selectedUser as UserOption).label,
              }
            : null,
        }),
      );
    }
  };

  return (
    <Accordion
      size="m"
      variant="standard"
      title={intl.formatMessage({ id: TRANSLATIONS[filter] })}
      defaultOpen={!!handlerHasValue}
      collapsable={filterQuantity > 1}
      testId={testId}
    >
      <SearchUser
        isMulti={TYPES.multiUser.includes(filter)}
        size="m"
        value={currentValue}
        options={options as UserOption[]}
        onChange={handleValueChange}
        placeholder={intl.formatMessage({ id: 'PLACEHOLDER_SEARCH_USERS' })}
        testId={testId}
        editor={settings?.editorAvatar}
        searchable
      />
    </Accordion>
  );
};

export default UserHandler;
